import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input, PriceInput, Select, Textarea, formatPrice } from '@artosera/ui';
import type { ContratLocationCreation } from '@artosera/shared';
import { useContact } from '../../api/contacts.js';
import {
  locationsApi,
  useLocation,
  useMutationLocation,
  type ContratLocationPresente,
  type LigneLocation,
} from '../../api/locations.js';
import { ouvrirUrlSignee } from '../../api/client.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { ChoixLigneLocation, ContratForm, jourCourantLocation, OptionForm } from './ContratForm.js';
import styles from './Locations.module.css';

type Geste =
  'activer' | 'suspendre' | 'reprendre' | 'rotations' | 'retour' | 'terminer' | 'depot' | 'lever';
function GesteDialog({
  contrat,
  geste,
  ligne,
  fermer,
}: {
  contrat: ContratLocationPresente;
  geste: Geste;
  ligne?: LigneLocation;
  fermer: () => void;
}) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerieSlug } = useShellContext();
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const [date, setDate] = useState(
    geste === 'terminer' ? (contrat.finLe ?? jourCourantLocation()) : jourCourantLocation(),
  );
  const [modeFin, setModeFin] = useState('terme');
  const [typeDepot, setTypeDepot] = useState('encaissement');
  const [montant, setMontant] = useState(contrat.depotGarantie.montantCentimes);
  const [motif, setMotif] = useState('');
  const [mode, setMode] = useState('');
  const [reference, setReference] = useState('');
  const [nouvelle, setNouvelle] = useState<
    (ContratLocationCreation['lignes'][number] & { titre: string }) | null
  >(null);
  const [erreur, setErreur] = useState('');
  const mutation = useMutationLocation(galerieSlug, (corps: Record<string, unknown>, appel) => {
    let chemin: string = geste;
    if (geste === 'retour') chemin = `lignes/${ligne?.id ?? ''}/retour`;
    if (geste === 'lever') chemin = `options/${ligne?.id ?? 'contrat'}/lever`;
    return locationsApi.geste(galerieSlug, contrat.id, chemin, corps, appel);
  });
  const [prixNet, setPrixNet] = useState<{ centimes: number; factureId: string } | null>(null);
  const option = ligne?.optionAchat ?? contrat.optionAchat;
  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!v) fermer();
      }}
      title={t(`locations.gestes.${geste}`)}
      closeLabel={t('commun:actions.fermer')}
    >
      {prixNet ? (
        <>
          <p>
            {t('locations.prixNet')} : {formatPrice(prixNet.centimes, langue, 'EUR')}
          </p>
          <Link to={`/${galerieSlug}/ventes/${prixNet.factureId}`}>
            {t('locations.ouvrirFacture')}
          </Link>
          <Button onClick={fermer}>{t('commun:actions.fermer')}</Button>
        </>
      ) : (
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            setErreur('');
            let corps: Record<string, unknown> = { version: contrat.version };
            if (['suspendre', 'reprendre', 'retour'].includes(geste)) corps['dateEffet'] = date;
            if (geste === 'terminer') corps = { ...corps, finLe: date, mode: modeFin, motif };
            if (geste === 'depot')
              corps = {
                ...corps,
                type: typeDepot,
                montantCentimes: montant,
                date,
                mode,
                reference,
                motif,
              };
            if (geste === 'rotations') {
              if (!nouvelle) {
                setErreur(t('locations.champsRequis'));
                return;
              }
              const { titre, ...nouvelleLigne } = nouvelle;
              void titre;
              corps = { ...corps, ligneId: ligne?.id, dateEffet: date, nouvelleLigne };
            }
            if (geste === 'lever') corps = {};
            void mutation
              .mutateAsync(corps)
              .then((r) => {
                if (geste === 'lever' && r.facture) {
                  setPrixNet({
                    centimes:
                      r.prixNetCentimes ??
                      r.facture.lignes.reduce(
                        (total, l) => total + l.prixUnitaireCentimes * l.quantite,
                        0,
                      ),
                    factureId: r.facture.id,
                  });
                } else fermer();
              })
              .catch((e) => setErreur(e instanceof Error ? e.message : t('commun:erreurReseau')));
          }}
        >
          <p>{t('locations.confirmerGeste')}</p>
          {ligne && <strong>{ligne.titre}</strong>}
          {!['activer', 'lever'].includes(geste) && (
            <label>
              {t('locations.dateEffet')}
              <Input required type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </label>
          )}
          {geste === 'terminer' && (
            <Select
              aria-label={t('locations.modeFin')}
              value={modeFin}
              onValueChange={setModeFin}
              options={['terme', 'resiliation', 'rupture'].map((v) => ({
                value: v,
                label: t(`locations.fins.${v}`),
              }))}
            />
          )}
          {geste === 'depot' && (
            <>
              <Select
                aria-label={t('locations.typeDepot')}
                value={typeDepot}
                onValueChange={setTypeDepot}
                options={['encaissement', 'restitution', 'retenue'].map((v) => ({
                  value: v,
                  label: t(`locations.depots.${v}`),
                }))}
              />
              <label>
                {t('locations.montant')}
                <PriceInput cents={montant} lang={langue} onCentsChange={setMontant} />
              </label>
              <label>
                {t('locations.modePaiement')}
                <Input value={mode} onChange={(e) => setMode(e.target.value)} />
              </label>
              <label>
                {t('locations.reference')}
                <Input value={reference} onChange={(e) => setReference(e.target.value)} />
              </label>
            </>
          )}
          {['terminer', 'depot'].includes(geste) && (
            <label>
              {t('locations.motif')}
              <Textarea value={motif} onChange={(e) => setMotif(e.target.value)} />
            </label>
          )}
          {geste === 'rotations' && (
            <>
              <ChoixLigneLocation galerieSlug={galerieSlug} onAjouter={setNouvelle} />
              {nouvelle && (
                <>
                  <strong>{nouvelle.titre}</strong>
                  <label>
                    {t('locations.loyer')}
                    <PriceInput
                      lang={langue}
                      cents={nouvelle.loyerMensuelHtCentimes}
                      onCentsChange={(v) => setNouvelle({ ...nouvelle, loyerMensuelHtCentimes: v })}
                    />
                  </label>
                  <label>
                    {t('locations.valeur')}
                    <PriceInput
                      lang={langue}
                      cents={nouvelle.valeurReferenceCentimes ?? 0}
                      onCentsChange={(v) =>
                        setNouvelle({ ...nouvelle, valeurReferenceCentimes: v })
                      }
                    />
                  </label>
                  <OptionForm
                    valeur={nouvelle.optionAchat}
                    changer={(o) => setNouvelle({ ...nouvelle, optionAchat: o })}
                  />
                </>
              )}
            </>
          )}
          {geste === 'lever' && option && (
            <>
              <p>
                {t('locations.prixConvenu')} :{' '}
                {formatPrice(option.prixConvenuCentimes, langue, 'EUR')}
              </p>
              <p>{t('locations.optionSansApercu')}</p>
            </>
          )}
          {erreur && <p role="alert">{erreur}</p>}
          <Button disabled={mutation.isPending} type="submit">
            {t('locations.confirmer')}
          </Button>
        </form>
      )}
    </Dialog>
  );
}

export function LocationDetailPage() {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const fiche = useLocation(galerieSlug, id);
  const contact = useContact(galerieSlug, fiche.data?.contrat.locataireId ?? null);
  const [geste, setGeste] = useState<{ type: Geste; ligne?: LigneLocation } | null>(null);
  const [edition, setEdition] = useState(false);
  const [fichier, setFichier] = useState<File | null>(null);
  const [depotOuvert, setDepotOuvert] = useState(false);
  const [erreur, setErreur] = useState('');
  const document = useMutationLocation(galerieSlug, (_: void, appel) =>
    locationsApi.geste(galerieSlug, id, 'document', {}, appel),
  );
  const signer = useMutationLocation(galerieSlug, (v: { version: number; fichier: File }, appel) =>
    locationsApi.deposer(galerieSlug, id, v.version, v.fichier, appel),
  );
  const lireSigne = useMutationLocation(galerieSlug, (_: void, appel) =>
    locationsApi.signe(galerieSlug, id, appel),
  );
  const modifier = useMutationLocation(galerieSlug, (corps: Record<string, unknown>, appel) =>
    locationsApi.modifier(galerieSlug, id, corps, appel),
  );
  if (fiche.isPending) return <p role="status">{t('commun:chargement')}</p>;
  if (!fiche.data || fiche.isError) return <p role="alert">{t('commun:erreurReseau')}</p>;
  const c = fiche.data.contrat;
  const peutEcrire = galerieALaction(galerie, 'locations:write');
  const peutFacturer = galerieALaction(galerie, 'locations:facturer');
  const prix = (v: number) => formatPrice(v, i18n.language === 'en' ? 'en' : 'fr', 'EUR');
  const ouvert = ['actif', 'suspendu'].includes(c.etatEffectif);
  const proposer = (type: Geste, ligne?: LigneLocation) =>
    setGeste({ type, ...(ligne ? { ligne } : {}) });
  const echec = (e: unknown) =>
    setErreur(e instanceof Error ? e.message : t('commun:erreurReseau'));
  const optionOuverte = (option: LigneLocation['optionAchat']) =>
    option !== null && !option.factureLeveeId && option.valableJusquAu >= jourCourantLocation();
  return (
    <section className={styles.page} data-ui="page">
      <header className={styles.entete}>
        <div>
          <h1>{c.numero}</h1>
          <p>{t(`locations.etats.${c.etatEffectif}`)}</p>
        </div>
        <div className={styles.actions}>
          {peutEcrire && (
            <>
              {c.etatEffectif === 'brouillon' && (
                <Button onClick={() => proposer('activer')}>{t('locations.gestes.activer')}</Button>
              )}
              {c.etatEffectif === 'actif' && (
                <Button onClick={() => proposer('suspendre')}>
                  {t('locations.gestes.suspendre')}
                </Button>
              )}
              {c.etatEffectif === 'suspendu' && (
                <Button onClick={() => proposer('reprendre')}>
                  {t('locations.gestes.reprendre')}
                </Button>
              )}
              {ouvert && (
                <Button onClick={() => proposer('terminer')}>
                  {t('locations.gestes.terminer')}
                </Button>
              )}
              {['brouillon', 'actif', 'suspendu'].includes(c.etatEffectif) && (
                <Button onClick={() => setEdition(true)}>{t('locations.modifier')}</Button>
              )}
              <Button onClick={() => proposer('depot')}>{t('locations.gestes.depot')}</Button>
            </>
          )}
          {peutFacturer && ouvert && optionOuverte(c.optionAchat) && (
            <Button onClick={() => proposer('lever')}>{t('locations.gestes.lever')}</Button>
          )}
          <Button
            disabled={document.isPending}
            onClick={() => {
              setErreur('');
              void document
                .mutateAsync()
                .then((r) => {
                  if (r.document) ouvrirUrlSignee(r.document.url);
                })
                .catch(echec);
            }}
          >
            {t('locations.pdf')}
          </Button>
        </div>
      </header>
      {erreur && <p role="alert">{erreur}</p>}
      <p>
        <Link to={`/${galerieSlug}/contacts/${c.locataireId}`}>
          {contact.data?.contact.nomAffiche ?? t('locations.locataire')}
        </Link>
      </p>
      <p>
        {c.adresseInstallation.ligne1} {c.adresseInstallation.ligne2} —{' '}
        {c.adresseInstallation.codePostal} {c.adresseInstallation.ville}{' '}
        {c.adresseInstallation.pays}
      </p>
      <p>
        {c.debutLe} — {c.finLe ?? t('locations.indeterminee')} · {t('locations.preavis')} :{' '}
        {c.preavisMois} · {t('locations.jourFacturation')} : {c.jourFacturation}
      </p>
      <div className={styles.liste}>
        {c.lignes.map((l) => (
          <article className={styles.carte} key={l.id}>
            <h2>
              <Link to={`/${galerieSlug}/oeuvres/${l.oeuvreId}`}>{l.titre}</Link>
            </h2>
            <p>
              {l.debutLe} — {l.finLe ?? '…'}
            </p>
            <p>
              {t('locations.loyer')} : {prix(l.loyerMensuelHtCentimes)} · {t('locations.valeur')} :{' '}
              {prix(l.valeurReferenceCentimes)}
            </p>
            {l.optionAchat && (
              <p>
                {t('locations.prixConvenu')} : {prix(l.optionAchat.prixConvenuCentimes)} ·{' '}
                {l.optionAchat.pourcentagePointsDeBase / 100}% · {t('locations.plafond')} :{' '}
                {prix(l.optionAchat.plafondCentimes)} · {l.optionAchat.valableJusquAu}
              </p>
            )}
            {l.retourConstateLe && (
              <p>
                {t('locations.retourConstate')} : {l.retourConstateLe}
                {l.ignoreeAuRetour && ` — ${t('locations.retourIgnore')}`}
              </p>
            )}
            <div className={styles.actions}>
              {peutEcrire &&
                ouvert &&
                !l.retourConstateLe &&
                !['vente', 'rotation'].includes(l.motifFin ?? '') && (
                  <Button onClick={() => proposer('rotations', l)}>
                    {t('locations.gestes.rotations')}
                  </Button>
                )}
              {peutEcrire &&
                c.etatEffectif !== 'brouillon' &&
                !l.retourConstateLe &&
                l.motifFin !== 'vente' && (
                  <Button onClick={() => proposer('retour', l)}>
                    {t('locations.gestes.retour')}
                  </Button>
                )}
              {peutFacturer && ouvert && !l.retourConstateLe && optionOuverte(l.optionAchat) && (
                <Button onClick={() => proposer('lever', l)}>{t('locations.gestes.lever')}</Button>
              )}
            </div>
          </article>
        ))}
      </div>
      <section>
        <h2>{t('locations.assurance')}</h2>
        <p>
          {t(`locations.${c.assurance.charge}`)} — {c.assurance.assureur} — {c.assurance.police}
        </p>
        <p>
          {t('locations.conditionsRotation')} : {c.conditionsRotation.periodiciteMois ?? '—'} ·{' '}
          {c.conditionsRotation.texte}
        </p>
        {c.optionAchat && (
          <p>
            {t('locations.prixConvenu')} : {prix(c.optionAchat.prixConvenuCentimes)} ·{' '}
            {c.optionAchat.pourcentagePointsDeBase / 100}% · {t('locations.plafond')} :{' '}
            {prix(c.optionAchat.plafondCentimes)} · {c.optionAchat.valableJusquAu}
          </p>
        )}
        <p>{c.notes}</p>
      </section>
      <section>
        <h2>{t('locations.depot')}</h2>
        <p>
          {t('locations.depotConvenu')} : {prix(c.depotGarantie.montantCentimes)} ·{' '}
          {t('locations.soldeDepot')} :{' '}
          {prix(
            c.depotGarantie.mouvements.reduce(
              (s, m) => s + (m.type === 'encaissement' ? m.montantCentimes : -m.montantCentimes),
              0,
            ),
          )}
        </p>
        {c.depotGarantie.mouvements.map((m, i) => (
          <p key={i}>
            {m.date} — {t(`locations.depots.${m.type}`)} — {prix(m.montantCentimes)} — {m.mode} —{' '}
            {m.reference} — {m.motif}
          </p>
        ))}
      </section>
      <section>
        <h2>{t('locations.echeances')}</h2>
        {fiche.data.echeances.map((e) => (
          <p key={e.debutPeriode}>
            {e.debutPeriode} — {e.finPeriode} · {prix(e.montantHtCentimes)}
          </p>
        ))}
        <Link to={`/${galerieSlug}/locations/echeances`}>{t('locations.echeances')}</Link>
      </section>
      <section className={styles.form}>
        <h2>{t('locations.contratSigne')}</h2>
        {c.contratSigne && (
          <Button
            disabled={lireSigne.isPending}
            onClick={() => {
              setErreur('');
              void lireSigne
                .mutateAsync()
                .then((r) => ouvrirUrlSignee(r.document.url))
                .catch(echec);
            }}
          >
            {c.contratSigne.nom}
          </Button>
        )}
        {peutEcrire && (
          <>
            <label>
              {t('locations.deposerSigne')}
              <Input
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => setFichier(e.target.files?.[0] ?? null)}
              />
            </label>
            <Button disabled={!fichier} onClick={() => setDepotOuvert(true)}>
              {t('locations.deposerSigne')}
            </Button>
          </>
        )}
      </section>
      {geste && (
        <GesteDialog
          contrat={c}
          geste={geste.type}
          {...(geste.ligne ? { ligne: geste.ligne } : {})}
          fermer={() => setGeste(null)}
        />
      )}
      {depotOuvert && (
        <Dialog
          open
          onOpenChange={setDepotOuvert}
          title={t('locations.deposerSigne')}
          closeLabel={t('commun:actions.fermer')}
        >
          <p>
            {t('locations.confirmerGeste')} {fichier?.name}
          </p>
          <Button
            disabled={signer.isPending}
            onClick={() => {
              if (!fichier) return;
              void signer
                .mutateAsync({ version: c.version, fichier })
                .then(() => {
                  setDepotOuvert(false);
                  setFichier(null);
                })
                .catch(echec);
            }}
          >
            {t('locations.confirmer')}
          </Button>
        </Dialog>
      )}
      {edition && (
        <Dialog
          open
          onOpenChange={setEdition}
          title={t('locations.modifier')}
          closeLabel={t('commun:actions.fermer')}
        >
          <ContratForm
            galerieSlug={galerieSlug}
            contrat={c}
            enCours={modifier.isPending}
            onSoumettre={async (corps) => {
              let modifications: Record<string, unknown> = { ...corps, version: c.version };
              if (c.etat !== 'brouillon')
                modifications = {
                  version: c.version,
                  adresseInstallation: corps.adresseInstallation,
                  assurance: corps.assurance,
                  notes: corps.notes,
                  conditionsRotation: corps.conditionsRotation,
                };
              await modifier.mutateAsync(modifications);
              setEdition(false);
            }}
          />
        </Dialog>
      )}
    </section>
  );
}
